// src/utils/cache/explore-tags-cache.js
// Explore tags batch loading and caching
// Single source of truth for explore tags caching and batch loading

import { LOGGING } from '/settings.js';
import { createBatchLoader, createSortedBatchKey } from './cache-helpers.js';
import { fetchWithCache } from './data-cache.js';

/**
 * Explore Tags Cache - In-memory cache for explore tags
 * Maps cacheKey (areaId-packId) -> tags array
 */
const exploreTagsCache = new Map();

/**
 * In-flight batch requests - tracks ongoing batch loads to prevent duplicate calls
 * Maps batchKey (areaId-sortedPackIds) -> Promise
 */
const inFlightBatchRequests = new Map();

/**
 * Orchestration promises to prevent redundant area-wide tag batch triggers
 * Maps areaId -> Promise
 */
const areaTagBatchOrchestrationPromises = new Map();

// Create batch loader using shared helper
const batchLoadTagsForPacksImpl = createBatchLoader({
    cache: exploreTagsCache,
    inFlightRequests: inFlightBatchRequests,
    getCacheKey: (packId, areaId) => `${areaId}-${packId}`,
    getBatchKey: (batch, areaId) => `${areaId}-${createSortedBatchKey(batch)}`,
    filterUncached: (packIds, cache, getCacheKey, areaId) => {
        return packIds.filter(packId => !cache.has(getCacheKey(packId, areaId)));
    },
    fetchBatch: async (batch, areaId) => {
        const packIdsParam = batch.join(',');
        const url = `/app_core/api/sqlite/spots-explore-3-tags.php?areaId=${areaId}&packIds=${packIdsParam}`;
        const data = await fetchWithCache(url);
        return data;
    },
    processResponse: (data, batch, cache, getCacheKey, allResults, getDefaultValue, areaId) => {
        // Store results in cache
        // Expected format: { "packId": [tags], ... }
        if (data && typeof data === 'object') {
            for (const [packIdStr, tags] of Object.entries(data)) {
                const packId = Number(packIdStr);
                const key = getCacheKey(packId, areaId);
                cache.set(key, tags);
                allResults.set(key, tags);
            }
        }
    },
    getCached: (packIds, cache, getCacheKey, areaId) => {
        const results = new Map();
        packIds.forEach(packId => {
            const key = getCacheKey(packId, areaId);
            if (cache.has(key)) {
                results.set(key, cache.get(key));
            }
        });
        return results;
    },
    getDefaultValue: () => [], // Empty array for packs with no tags
    cacheName: 'Explore Tags Cache',
    maxBatchSize: 50
});

/**
 * Orchestrates the full area-wide tag batch load.
 * Ensures packs are fetched first, then the batch tag fetch is triggered for all of them.
 * @param {number} areaId 
 * @returns {Promise<void>}
 */
export async function triggerAreaTagBatch(areaId) {
    const aId = Number(areaId);
    if (!aId) return;

    if (areaTagBatchOrchestrationPromises.has(aId)) {
        return areaTagBatchOrchestrationPromises.get(aId);
    }

    const promise = (async () => {
        try {
            // 1. Get packs for the area
            const { getPacksForArea } = await import('../spot-queries.js');
            const packs = await getPacksForArea(aId);

            if (!packs || packs.length === 0) {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Explore Tags Cache] No packs found for area ${aId}, skipping tag batch.`);
                }
                return;
            }

            const packIds = packs.map(p => p.id).filter(Boolean);

            // 2. Trigger the batch tag load
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Explore Tags Cache] Orchestrating tag batch for Area: ${aId}, Packs: ${packIds.join(',')}`);
            }

            await batchLoadTagsForPacks(aId, packIds);

            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Explore Tags Cache] Orchestrated tag batch completed for Area: ${aId}`);
            }
        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.error(`[Explore Tags Cache] Orchestrated tag batch failed for Area: ${aId}`, error);
            }
            // Clear promise on error to allow retry
            areaTagBatchOrchestrationPromises.delete(aId);
            throw error;
        }
    })();

    areaTagBatchOrchestrationPromises.set(aId, promise);
    return promise;
}

/**
 * Batch load tags for multiple packs in a single API call
 * @param {number} areaId - Area ID
 * @param {Array<number>} packIds - Array of pack IDs
 * @returns {Promise<Map<number, Array>>} Map of packId -> tags array
 */
export async function batchLoadTagsForPacks(areaId, packIds) {
    if (!areaId || !packIds || !Array.isArray(packIds) || packIds.length === 0) {
        return new Map();
    }
    const results = await batchLoadTagsForPacksImpl(packIds, areaId);
    // Convert Map keys (cache keys) back to packIds for compatibility
    const resultMap = new Map();
    results.forEach((tags, cacheKey) => {
        const packId = Number(cacheKey.split('-')[1]);
        resultMap.set(packId, tags);
    });
    return resultMap;
}

/**
 * Get tags for a single pack (cache-first approach)
 * @param {number} areaId - Area ID
 * @param {number} packId - Pack ID
 * @returns {Promise<Array>} Array of tag objects
 */
export async function getTagsForPack(areaId, packId) {
    const aId = Number(areaId);
    const pId = Number(packId);
    const cacheKey = `${aId}-${pId}`;

    // NEW: Always ensure area-wide tag batch is triggered and awaited first.
    // This blocks ANY individual tag request until the "Big Batch" has arrived.
    await triggerAreaTagBatch(aId);

    // Check cache first
    if (exploreTagsCache.has(cacheKey)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Explore Tags Cache] Cache HIT for pack ${pId}`);
        }
        return exploreTagsCache.get(cacheKey);
    }

    // Check if this pack is part of an in-flight batch
    // We need to check all in-flight batches to see if this packId is included
    for (const [batchKey, promise] of inFlightBatchRequests.entries()) {
        // batchKey format: "areaId-packId1,packId2,packId3"
        const [batchAreaId, packIdsStr] = batchKey.split('-');
        if (Number(batchAreaId) === areaId && packIdsStr) {
            const packIds = packIdsStr.split(',').map(Number);
            if (packIds.includes(packId)) {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Explore Tags Cache] Pack ${packId} is part of in-flight batch, waiting...`);
                }
                // Wait for the batch to complete
                await promise;
                // Check cache again after batch completes
                if (exploreTagsCache.has(cacheKey)) {
                    return exploreTagsCache.get(cacheKey);
                }
            }
        }
    }

    // If not cached and not in-flight, trigger a batch load for this single pack
    // This ensures we use the batch endpoint even for single requests
    const result = await batchLoadTagsForPacks(areaId, [packId]);
    return result.get(packId) || [];
}

/**
 * Preload tags for multiple packs (background, non-blocking)
 * @param {number} areaId - Area ID
 * @param {Array<number>} packIds - Array of pack IDs
 */
export function preloadTagsForPacks(areaId, packIds) {
    if (!areaId || !packIds || !Array.isArray(packIds) || packIds.length === 0) {
        return;
    }

    // Filter out already cached packs
    const uncachedPackIds = packIds.filter(packId => {
        const cacheKey = `${areaId}-${packId}`;
        return !exploreTagsCache.has(cacheKey);
    });

    if (uncachedPackIds.length === 0) {
        return;
    }

    // Preload in background (don't await)
    batchLoadTagsForPacks(areaId, uncachedPackIds).catch(error => {
        console.error(`[Explore Tags Cache] Error preloading tags:`, error);
    });
}

/**
 * Get cached tags for multiple packs
 * @param {number} areaId - Area ID
 * @param {Array<number>} packIds - Array of pack IDs
 * @returns {Map<number, Array>} Map of packId -> tags array
 */
function getCachedTags(areaId, packIds) {
    const results = new Map();
    for (const packId of packIds) {
        const cacheKey = `${areaId}-${packId}`;
        if (exploreTagsCache.has(cacheKey)) {
            results.set(packId, exploreTagsCache.get(cacheKey));
        }
    }
    return results;
}

/**
 * Clear tags cache for specific packs
 * @param {number} areaId - Area ID (optional, clears all if not provided)
 * @param {Array<number>} packIds - Array of pack IDs (optional, clears all packs for area if not provided)
 */
export function clearExploreTagsCache(areaId = null, packIds = null) {
    if (areaId === null) {
        // Clear all
        exploreTagsCache.clear();
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Explore Tags Cache] Cleared all tags cache');
        }
    } else if (packIds === null) {
        // Clear all packs for this area
        const keysToDelete = [];
        for (const key of exploreTagsCache.keys()) {
            if (key.startsWith(`${areaId}-`)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => exploreTagsCache.delete(key));
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Explore Tags Cache] Cleared tags cache for area ${areaId}`);
        }
    } else {
        // Clear specific packs
        for (const packId of packIds) {
            const cacheKey = `${areaId}-${packId}`;
            exploreTagsCache.delete(cacheKey);
        }
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Explore Tags Cache] Cleared tags cache for area ${areaId}, packs:`, packIds);
        }
    }
}

